import { RootState } from '@AppState/redux-store'
import { Button, CheckboxGroup, Flex, TextField } from '@radix-ui/themes'
import { Form } from 'radix-ui'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  CreateUserRequest,
  DisplayUser,
  UpdateUserRequest,
} from '@Generated/back-office-client/src'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { UsersServiceAPI } from '@API/data-services/users-service'
import { Routes } from '@Components/main-app/types'

export const UpsertUser = () => {
  const nav = useNavigate()
  const params = useParams()
  const location = useLocation()
  const [rolesState, setRolesState] = useState<string[]>()
  const [rolesError, setRolesError] = useState<boolean>(!!(rolesState && !rolesState.length))
  const { activeCustomerId } = useSelector((state: RootState) => state.users)

  const editUserId = params?.authProviderId
  const userForEdit = editUserId ? (location.state as DisplayUser) : null

  const queryClient = useQueryClient()

  const { data: roles } = useQuery({
    queryKey: [`${activeCustomerId}-roles`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: () => UsersServiceAPI.getCustomerRoles(),
  })

  const { mutate: upsertUser, isSuccess } = useMutation({
    mutationFn: (upsertUserRequest: CreateUserRequest | UpdateUserRequest) => {
      return editUserId
        ? UsersServiceAPI.updateUser(editUserId, upsertUserRequest)
        : UsersServiceAPI.createUser(upsertUserRequest as CreateUserRequest)
    },
  })

  useEffect(() => {
    // If we don't have the data in state for the user we're trying to load, go back to the users list
    if (editUserId && !userForEdit) {
      nav(Routes.USERS)
    } else if (userForEdit && roles?.length) {
      setRolesState(userForEdit.roles.map((r) => r.id))
    }
  }, [editUserId, nav, roles?.length, userForEdit])

  useEffect(() => {
    if (isSuccess) {
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-users`] })
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-extra-user-data`] })
      nav(Routes.USERS)
    }
  }, [activeCustomerId, isSuccess, nav, queryClient])

  return (
    <Form.Root
      onSubmit={async (e) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget).entries()
        const data = Object.fromEntries(formData)

        if (!rolesState || !rolesState.length) {
          setRolesError(true)
          return
        }

        const upsertUserRequest: CreateUserRequest | UpdateUserRequest = {
          email: data.email as string,
          name: data.name as string,
          roleIds: rolesState,
        }

        upsertUser(upsertUserRequest)
      }}>
      <Flex direction="column" gap="4">
        <Form.Field name="name">
          <Flex justify="between">
            <Form.Label>Full Name</Form.Label>
            <Form.Message className="FormMessage" match="valueMissing">
              Name is required
            </Form.Message>
          </Flex>

          <Form.Control asChild required>
            <TextField.Root defaultValue={userForEdit?.name} />
          </Form.Control>
        </Form.Field>

        <Form.Field name="email">
          <Flex justify="between">
            <Form.Label>Email</Form.Label>
            <Form.Message className="FormMessage" match="valueMissing">
              Email is required
            </Form.Message>
            <Form.Message className="FormMessage" match="typeMismatch">
              Please provide a valid email
            </Form.Message>
          </Flex>

          <Form.Control asChild type="email" required>
            <TextField.Root defaultValue={userForEdit?.email} disabled={!!userForEdit?.email} />
          </Form.Control>
        </Form.Field>

        <Form.Field name="roles">
          <Flex justify="between">
            <Form.Label>Roles</Form.Label>
            {rolesError ? (
              <Form.Message className="FormMessage">At least one role is required</Form.Message>
            ) : null}
          </Flex>

          {roles?.length ? (
            <CheckboxGroup.Root
              value={rolesState ?? []}
              onValueChange={(checkedValues) => {
                setRolesState(checkedValues)
                if (checkedValues.length) {
                  setRolesError(false)
                }
              }}>
              {roles.map((r) => (
                <CheckboxGroup.Item key={r.id} value={r.id}>
                  {r.name}
                </CheckboxGroup.Item>
              ))}
            </CheckboxGroup.Root>
          ) : (
            <Flex>No roles exist to be able to create a user</Flex>
          )}
        </Form.Field>

        <Form.Submit asChild>
          <Button>Submit</Button>
        </Form.Submit>
      </Flex>
    </Form.Root>
  )
}
